import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'
import { createStore, del, get, set } from 'idb-keyval'
import { APP_VERSION } from '@/lib/version'

// persists a deliberately small slice of the query cache so the add-expense form still
// works with no server: its account, category, group, template and rate lookups come from
// here. nothing else is persisted — the app shows an offline state rather than presenting
// stale figures as current, which for balances and reports would be worse than showing
// nothing.

// `expense-templates` is here because applying a template is the fastest way to record an
// expense on a phone, which is exactly the situation this feature is for.
const PERSISTED_QUERY_ROOTS = [
  'accounts',
  'categories',
  'groups',
  'exchange-rates',
  'expense-templates',
]

// a week is long enough to cover a phone left untouched over a holiday, and short enough
// that a cache this old is not silently trusted forever.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

const CACHE_KEY = 'react-query-cache'

// its own database name — see the note in outboxDb.ts on why idb-keyval stores must not
// share one
function createIdbPersister(): Persister {
  const store = createStore('fp3-query-cache', 'cache')
  return {
    persistClient: (client: PersistedClient) => set(CACHE_KEY, client, store),
    restoreClient: () => get<PersistedClient>(CACHE_KEY, store),
    removeClient: () => del(CACHE_KEY, store),
  }
}

// stands in where IndexedDB is unavailable (jsdom, a browser privacy mode). the app then
// simply has no offline lookups, rather than failing to start.
const noopPersister: Persister = {
  persistClient: async () => {},
  restoreClient: async () => undefined,
  removeClient: async () => {},
}

const persister = typeof indexedDB === 'undefined' ? noopPersister : createIdbPersister()

// wipes the cached lookups. the persisted slice is per-user data (account names, category
// names, group names, templates) with no user in its key, so it MUST be dropped whenever a
// session ends — including the involuntary 401 path — or the next person to sign in on this
// device rehydrates the previous user's data and, with a 5 minute staleTime, is not even
// refetched out of it. offline they would see nothing else.
export const clearPersistedQueryCache = (): Promise<void> =>
  Promise.resolve(persister.removeClient())

export const queryPersistOptions = {
  persister,
  maxAge: MAX_AGE_MS,
  // the build stamp busts the cache on deploy: a release that changes a DTO shape must not
  // rehydrate yesterday's shape into today's components.
  buster: APP_VERSION,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { queryKey: readonly unknown[]; state: { status: string } }) => {
      const root = query.queryKey[0]
      return (
        query.state.status === 'success' &&
        typeof root === 'string' &&
        PERSISTED_QUERY_ROOTS.includes(root)
      )
    },
  },
}
